import {defineRecipe} from '@chakra-ui/react3'

/**
 * The display the wrapper takes at the viewport where the entry is shown.
 *
 * `contents` is the default because most of the tree wants exactly that: the
 * wrapper contributes no box and the wrapped element keeps whatever layout role
 * it had in its parent. But a `display: contents` element generates no box at
 * all, so `opacity` and `translate` on it have nothing to apply to — which is
 * why the flyout's staggered rows, every one of them wrapped here, cannot be
 * animated directly while they carry it.
 *
 * Naming the shown display as a custom property hands that choice back to the
 * element being wrapped: a slot that needs a real box sets
 * `--navigation-hide-display` and the wrapper honours it. A custom property is
 * what makes that work at all. `asChild` merges the wrapper's declarations into
 * the same emitted class as the child's, with the wrapper's last, so a `display`
 * declared by the child would simply lose; a property the child *sets* and the
 * wrapper *reads* resolves on the element whichever order they land in.
 */
const SHOWN = 'var(--navigation-hide-display, contents)'

/**
 * Recipe for `NavigationHide`.
 *
 * The CMS `hide` field is applied as responsive CSS rather than by branching on
 * a breakpoint in JavaScript: server and client markup stay identical, and a
 * resize never remounts the navigation tree. A hidden entry remains in the DOM
 * but `display: none` takes it out of the accessibility tree along with its box.
 *
 * Each hiding variant restores `SHOWN` at the viewport where the entry appears,
 * so the declaration is total and never depends on rule order against the
 * wrapped element's own styles. The hidden value stays a plain keyword rather
 * than moving into a media-only rule: Chakra's `hideFrom`/`hideBelow` express
 * the same intent, but they emit `display: none` inside `@media screen and (…)`,
 * and jsdom applies a media rule only when its media list is literally `screen`.
 * The hiding would then be invisible to the tests that assert a hidden entry has
 * left the accessibility tree, which would keep passing while checking nothing.
 */
export default defineRecipe({
    className: 'navigation-hide',
    base: {
        display: SHOWN
    },
    variants: {
        hide: {
            none: {},
            mobile: {
                display: {base: 'none', s: SHOWN}
            },
            desktop: {
                display: {base: SHOWN, s: 'none'}
            }
        }
    },
    defaultVariants: {
        hide: 'none'
    }
})
